import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import LeadForm from "./LeadForm";
import { track } from "../lib/analytics";

const SESSION_KEY = "sg_popup_shown";

export default function LeadPopup() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    const show = () => {
      setOpen(true);
      track("popup_shown", {});
    };
    const autoShow = () => {
      if (!sessionStorage.getItem(SESSION_KEY)) {
        sessionStorage.setItem(SESSION_KEY, "1");
        show();
      }
    };
    // Desktop: open after 6 seconds. Phones: open only once the visitor has
    // scrolled halfway down, so the popup never covers a page on arrival.
    let timer;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= 0.5) {
        window.removeEventListener("scroll", onScroll);
        autoShow();
      }
    };
    if (window.matchMedia("(min-width: 1024px)").matches) timer = setTimeout(autoShow, 6000);
    else window.addEventListener("scroll", onScroll, { passive: true });
    const onManual = () => {
      clearTimeout(timer);
      show();
    };
    window.addEventListener("open-lead-popup", onManual);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("open-lead-popup", onManual);
    };
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          data-testid="lead-popup-overlay"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-ink p-6 sm:rounded-3xl sm:p-8 ember-glow"
            data-testid="lead-popup"
          >
            <button
              data-testid="lead-popup-close"
              onClick={close}
              aria-label="Close popup"
              className="absolute right-5 top-5 text-white/40 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            {!submitted ? (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
                  Free Website Strategy
                </p>
                <h2 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                  Need More Customers From Your Website?
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-white/50">
                  Tell me about your business and get a website strategy designed around generating enquiries.
                </p>
                <div className="mt-6">
                  <LeadForm
                    extended
                    source="popup"
                    testIdPrefix="popup-form"
                    submitLabel="Get My Free Website Plan"
                    onSubmitted={(form, waUrl) => setSubmitted(waUrl)}
                  />
                </div>
              </>
            ) : (
              <div className="py-6 text-center" data-testid="lead-popup-success">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#25D366]/15">
                  <MessageCircle className="h-8 w-8 text-[#25D366]" />
                </div>
                <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Thanks! Let's Discuss Your Business.
                </h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/50">
                  Your details are ready. Continue on WhatsApp to send them instantly — you'll get a reply with your free website plan.
                </p>
                <a
                  href={submitted}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="popup-whatsapp-continue-btn"
                  onClick={() => track("popup_whatsapp_continue", {})}
                  className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1eb856]"
                >
                  <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
                </a>
                <button
                  data-testid="popup-success-close"
                  onClick={close}
                  className="mt-4 text-xs text-white/40 underline-offset-2 hover:underline"
                >
                  I'll do it later
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
